import React, { useState, useRef, useEffect, useMemo } from "react";
import { View, Text, TextInput, StyleSheet, Platform, TouchableOpacity } from "react-native";
import { useTranslation } from "../../../../../i18n/useTranslation";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import { inputAssistDisabledProps } from "../../../common/FloatingTextInputComponent";

type OtpVerificationStepProps = {
  onContinue?: () => void;
  onResendCode?: () => void;
  testID?: string | undefined;
};

export default function OtpVerificationStep({ onContinue, onResendCode, testID }: OtpVerificationStepProps) {
  const { t } = useTranslation();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const inputs = useRef<(TextInput | null)[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [error, setError] = useState("");
  const phoneNumber = useSessionStore((s: SessionState) => (s as any).phoneNumber);

  const borderColor = isFocused
    ? Colors.black
    : Colors.textInputBackground;

  const borderWidth = isFocused ? 1 : 1;

  // Format phone number for display: +1 (610)-555-1234
  const formattedPhoneNumber = useMemo(() => {
    if (!phoneNumber) return "";
    const cleaned = phoneNumber.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `+1 (${cleaned.slice(0, 3)})-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phoneNumber;
  }, [phoneNumber]);

  // Focus first input on mount
  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  const handleChange = (text: string, index: number) => {
    // Validate that text contains only digits
    const isValid = text.length === 0 || text.split('').every(char => {
      const charCode = char.charCodeAt(0);
      return charCode >= 48 && charCode <= 57; // '0' is 48, '9' is 57
    });
    
    if (isValid) {
      const newOtp = [...otp];
      newOtp[index] = text;
      setOtp(newOtp);
      setError(""); // Clear error on input

      // Move to next box automatically
      if (text && index < 5) {
        inputs.current[index + 1]?.focus();
      }

      // Backspace handling
      if (!text && index > 0) {
        inputs.current[index - 1]?.focus();
      }

      // When all 6 digits are entered, trigger verification
      if (newOtp.every(d => d !== "")) {
        console.log('OTP COMPLETE====>', newOtp.join(""));
        // TODO: Verify OTP with backend
        // For now, just proceed
        onContinue?.();
      }
    }
  };

  const handleResendCode = () => {
    // TODO: Call API to resend OTP code
    console.log('Resending OTP code...');
    onResendCode?.();
  };

  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.title}>{t("OTP_VERIFICATION_TITLE") || "Enter your code"}</Text>
      <Text style={styles.subtitle}>
        {t("OTP_VERIFICATION_SUBTITLE") || `We sent a security code to ${formattedPhoneNumber || "your phone number"}.`}
      </Text>

      <View style={styles.otpContainer}>
        {otp.map((digit, index) => (
          <View key={index} style={[styles.otpBox, { borderColor: borderColor, borderWidth: borderWidth }]}>
            <TextInput
              testID={`otpInput${index}`}
              ref={(el) => (inputs.current[index] = el)}
              style={styles.otpInput}
              keyboardType="number-pad"
              maxLength={1}
              value={digit}
              onChangeText={(text) => handleChange(text, index)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              {...inputAssistDisabledProps}
            />
          </View>
        ))}
      </View>

      <TouchableOpacity onPress={handleResendCode} style={styles.resendContainer}>
        <Text style={styles.resendText}>{t("SEND_NEW_CODE") || "Send a new code"}</Text>
      </TouchableOpacity>

      {error ? (
        <Text style={styles.error}>{error}</Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 32
  },
  otpContainer: {
    flexDirection: "row",
    marginBottom: 16,
    justifyContent: 'space-between',
  },
  otpInput: {
    width: '100%',
    height: '100%',
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    color: Colors.titleTextColor,
    backgroundColor: Colors.white,
    ...(Platform.OS === 'web' && {
      outlineWidth: 0,
      outline: 'none',
      border: 'none',
      boxShadow: 'none',
    } as any),
  },
  otpBox: {
    width: 48,
    height: 56,
    borderRadius: 8,
    backgroundColor: Colors.white,
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 4,
  },
  resendContainer: {
    alignItems: 'center',
    marginTop: 8,
  },
  resendText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 16,
    fontWeight: '500',
    color: Colors.titleTextColor,
    textDecorationLine: 'underline',
  },
  error: {
    color: Colors.errorRedColor,
    fontSize: 12,
    marginTop: 8,
    fontFamily: ComponentStyles.textFamily,
  },
});

